import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { ReactNode } from 'react';

import { useImpactScoreWeights } from '@/api/settings';
import { Spinner } from '@/components/loaders';
import { Panel } from '@/components/panels';
import { Text } from '@/components/typography';

import CompanyImpactScoringSettingsForm from './CompanyImpactScoringSettingsForm';
import CompanySettingsActionBar from './CompanySettingsActionBar';

type Props = {
  canEdit: boolean;
  startContent?: ReactNode;
  onDirtyChange?: (isDirty: boolean) => void;
  onEditModeChange?: (isEditMode: boolean) => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
};

const CompanyImpactScoringSettings = ({
  canEdit,
  startContent,
  onDirtyChange,
  onEditModeChange,
  onSubmittingChange,
}: Props) => {
  const { settings, isLoading, error, mutate } = useImpactScoreWeights();

  if (!isLoading && !error && settings) {
    return (
      <CompanyImpactScoringSettingsForm
        settings={settings}
        mutate={mutate}
        canEdit={canEdit}
        startContent={startContent}
        onDirtyChange={onDirtyChange}
        onEditModeChange={onEditModeChange}
        onSubmittingChange={onSubmittingChange}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <CompanySettingsActionBar
        isEditMode={false}
        isSaveEnabled={false}
        isSaving={false}
        startContent={startContent}
        showActions={false}
        onEditModeChange={() => {}}
        onCancel={() => {}}
      />

      {isLoading ? (
        <div className="flex flex-col items-center gap-4 py-16">
          <Spinner variant="spoke" size="lg" />
          <Text variant="subhead">
            <Trans>Loading data</Trans>
          </Text>
        </div>
      ) : (
        <Panel title={t`Impact Scoring`}>
          <Trans>Impact scoring settings could not be loaded.</Trans>
        </Panel>
      )}
    </div>
  );
};

export default CompanyImpactScoringSettings;
